<template>
  <div>
    <!-- Statistics Cards -->
    <CRow class="mb-4">
      <CCol sm="6" md="3">
        <CCard class="text-white bg-primary">
          <CCardBody>
            <div class="text-value-lg">{{ stats.total }}</div>
            <div>Total Consent Records</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" md="3">
        <CCard class="text-white bg-success">
          <CCardBody>
            <div class="text-value-lg">{{ stats.acceptanceRate }}%</div>
            <div>Acceptance Rate</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" md="3">
        <CCard class="text-white bg-warning">
          <CCardBody>
            <div class="text-value-lg">{{ stats.customized }}</div>
            <div>Customized Consents</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm="6" md="3">
        <CCard class="text-white bg-info">
          <CCardBody>
            <div class="text-value-lg">{{ stats.todayCount }}</div>
            <div>Today's Consents</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Main Logs Table -->
    <CCard>
      <CCardHeader>
        <h4 class="mb-0">
          Cookie Consent Logs
        </h4>
        <div class="card-header-actions">
          <CButton color="success" size="sm" @click="exportLogs" class="mr-2">
            Export CSV
          </CButton>
          <CButton color="primary" size="sm" @click="refreshLogs">
            Refresh
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <!-- Filters -->
        <CRow class="mb-3">
          <CCol md="3">
            <CInput
              v-model="filters.search"
              placeholder="Search by email..."
              @input="debounceSearch"
            />
          </CCol>
          <CCol md="3">
            <multiselect
              v-model="filters.consentType"
              :options="consentTypeOptions"
              :multiple="false"
              :clear-on-select="false"
              :close-on-select="true"
              track-by="value"
              label="text"
              :searchable="false"
              placeholder="Select consent type"
              :preselect-first="false"
              @input="fetchLogs"
            ></multiselect>
          </CCol>
          <CCol md="2">
            <CInput
              type="date"
              v-model="filters.startDate"
              placeholder="Start Date"
              @change="fetchLogs"
            />
          </CCol>
          <CCol md="2">
            <CInput
              type="date"
              v-model="filters.endDate"
              placeholder="End Date"
              @change="fetchLogs"
            />
          </CCol>
          <CCol md="2">
            <CButton color="secondary" @click="clearFilters" block>
              Clear
            </CButton>
          </CCol>
        </CRow>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-2">Loading consent logs...</p>
        </div>

        <!-- Data Table -->
        <CDataTable
          v-else
          :items="logs"
          :fields="fields"
          :items-per-page="itemsPerPage"
          :pagination="{ doubleArrows: false, align: 'center'}"
          hover
          sorter
          table-filter
          :loading="loading"
        >
          <!-- Timestamp -->
          <template #timestamp="{item}">
            <td>
              {{ formatDate(item.timestamp) }}
            </td>
          </template>

          <!-- User Info -->
          <template #user="{item}">
            <td>
              <div>
                <strong>{{ item.useremail || 'Guest' }}</strong>
                <br>
                <small class="text-muted">{{ item.useremail || item.sessionId }}</small>
              </div>
            </td>
          </template>

          <!-- Consent Type -->
          <template #consentType="{item}">
            <td>
              <CBadge :color="getConsentBadgeColor(item)">
                {{ getConsentType(item) }}
              </CBadge>
            </td>
          </template>

          <!-- Categories -->
          <template #categories="{item}">
            <td>
              <div class="d-flex flex-wrap">
                <CBadge color="dark" class="mr-1 mb-1">
                  Necessary ✓
                </CBadge>
                <CBadge 
                  :color="item.categories.analytics ? 'success' : 'secondary'" 
                  class="mr-1 mb-1"
                >
                  Analytics {{ item.categories.analytics ? '✓' : '✗' }}
                </CBadge>
                <CBadge 
                  :color="item.categories.marketing ? 'info' : 'secondary'" 
                  class="mr-1 mb-1"
                >
                  Marketing {{ item.categories.marketing ? '✓' : '✗' }}
                </CBadge>
                <CBadge 
                  :color="item.categories.preferences ? 'warning' : 'secondary'" 
                  class="mr-1 mb-1"
                >
                  Preferences {{ item.categories.preferences ? '✓' : '✗' }}
                </CBadge>
              </div>
            </td>
          </template>

          <!-- Device Info -->
          <template #device="{item}">
            <td>
              <small>{{ parseUserAgent(item.userAgent) }}</small>
            </td>
          </template>

          <!-- URL -->
          <template #url="{item}">
            <td>
              <small class="text-truncate d-inline-block" style="max-width: 200px;">
                {{ item.url }}
              </small>
            </td>
          </template>

          <!-- Actions -->
          <template #actions="{item}">
            <td>
              <CButton
                color="primary"
                variant="ghost"
                size="sm"
                @click="viewDetails(item)"
              >
                View
              </CButton>
            </td>
          </template>
        </CDataTable>

        <!-- Pagination Info -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {{ logs.length }} of {{ totalRecords }} records
            <span v-if="filters.startDate || filters.endDate" class="text-muted ml-2">
              ({{ filters.startDate || 'Start' }} to {{ filters.endDate || 'Today' }})
            </span>
          </div>
          <div>
            <CSelect v-model="itemsPerPage" @change="fetchLogs" style="width: auto;">
              <option :value="10">10 per page</option>
              <option :value="25">25 per page</option>
              <option :value="50">50 per page</option>
              <option :value="100">100 per page</option>
            </CSelect>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Details Modal -->
    <CModal
      :show.sync="showDetailsModal"
      :no-close-on-backdrop="false"
      size="lg"
    >
      <template #header>
        <h5 class="modal-title">Consent Log Details</h5>
      </template>
      <div v-if="selectedLog">
        <CRow>
          <CCol md="6">
            <p><strong>Timestamp:</strong><br>{{ formatDate(selectedLog.timestamp) }}</p>
            <p><strong>User Email:</strong><br>{{ selectedLog.useremail || 'Not logged in' }}</p>
         
            <p><strong>Session ID:</strong><br>{{ selectedLog.sessionId }}</p>
          </CCol>
          <CCol md="6">
            <p><strong>Categories Consented:</strong></p>
            <ul>
              <li>Necessary: ✓ (Always)</li>
              <li>Analytics: {{ selectedLog.categories.analytics ? '✓' : '✗' }}</li>
              <li>Marketing: {{ selectedLog.categories.marketing ? '✓' : '✗' }}</li>
              <li>Preferences: {{ selectedLog.categories.preferences ? '✓' : '✗' }}</li>
            </ul>
          </CCol>
        </CRow>
        <hr>
        <p><strong>URL:</strong><br>{{ selectedLog.url }}</p>
        <p><strong>User Agent:</strong><br>{{ selectedLog.userAgent }}</p>
        <p><strong>Logged At:</strong><br>{{ selectedLog.createdAt ? formatDate(selectedLog.createdAt) : 'N/A' }}</p>
      </div>
      <template #footer>
        <CButton color="secondary" @click="showDetailsModal = false">Close</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import cookieConsentLogsService from '@/services/cookieConsentLogsService'
import Multiselect from 'vue-multiselect'

export default {
  name: 'CookieConsentLogs',
  components: {
    Multiselect
  },
  data() {
    return {
      logs: [],
      loading: false,
      showDetailsModal: false,
      selectedLog: null,
      totalRecords: 0,
      itemsPerPage: 25,
      currentPage: 1,
      filters: {
        search: '',
        consentType: { value: '', text: 'All Consent Types' },
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate()
      },
      consentTypeOptions: [
        { value: '', text: 'All Consent Types' },
        { value: 'accepted', text: 'Accepted All' },
        { value: 'rejected', text: 'Rejected All' },
        { value: 'customized', text: 'Customized' }
      ],
      stats: {
        total: 0,
        acceptanceRate: 0,
        customized: 0,
        todayCount: 0
      },
      fields: [
        { key: 'timestamp', label: 'Date & Time', _style: 'min-width:150px' },
        { key: 'user', label: 'User', _style: 'min-width:150px' },
        { key: 'consentType', label: 'Type' },
        { key: 'categories', label: 'Categories', _style: 'min-width:250px' },
        { key: 'device', label: 'Device' },
        { key: 'url', label: 'Page URL' },
        { key: 'actions', label: '', _style: 'width:50px' }
      ],
      searchTimeout: null
    }
  },
  mounted() {
    this.checkManagerAccess()
    this.fetchLogs()
    this.fetchStats()
  },
  methods: {
    getDefaultStartDate() {
      // Get date 7 days ago
      const date = new Date()
      date.setDate(date.getDate() - 7)
      return date.toISOString().split('T')[0]
    },
    
    getDefaultEndDate() {
      // Get today's date
      const date = new Date()
      return date.toISOString().split('T')[0]
    },
    
    checkManagerAccess() {
      const user = localStorage.getItem('user')
      if (!user || (user !== 'manager' && user !== 'admin')) {
        this.$router.push('/dashboard')
        this.$store.dispatch('addNotification', {
          type: 'error',
          message: 'Access denied. Manager privileges required.'
        })
      }
    },
    
    async fetchLogs() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          search: this.filters.search,
          type: this.filters.consentType.value,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate
        }
        
        // Use the service to fetch logs
        const response = await cookieConsentLogsService.getLogs(params)
        
        if (response && response.success) {
          this.logs = response.data
          this.totalRecords = response.pagination ? response.pagination.total : response.data.length
        } else {
          // Fallback to mock data if API not ready
          const mockResponse = await this.getMockLogs(params)
          this.logs = mockResponse.data
          this.totalRecords = mockResponse.total
        }
      } catch (error) {
        console.error('Error fetching consent logs:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchStats() {
      try {
        // Use the service to fetch stats for the selected period
        const params = {
          startDate: this.filters.startDate,
          endDate: this.filters.endDate
        }
        const response = await cookieConsentLogsService.getStats(params)
        
        if (response && response.success && response.stats) {
          this.stats = {
            total: response.stats.total || 0,
            acceptanceRate: response.stats.acceptanceRate || 0,
            customized: response.stats.customized || 0,
            todayCount: response.stats.todayCount || 0
          }
        } else {
          // Fallback to mock stats if API not ready
          this.stats = {
            total: 0,
            acceptanceRate: 0,
            customized: 0,
            todayCount: 0
          }
        }
      } catch (error) {
        console.error('Error fetching stats:', error)
        // Use mock stats as fallback
        this.stats = {
          total: 0,
          acceptanceRate: 0,
          customized: 0,
          todayCount: 0
        }
      }
    },


    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getConsentType(item) {
      const { analytics, marketing, preferences } = item.categories
      if (analytics && marketing && preferences) return 'Accepted All'
      if (!analytics && !marketing && !preferences) return 'Rejected All'
      return 'Customized'
    },

    getConsentBadgeColor(item) {
      const type = this.getConsentType(item)
      if (type === 'Accepted All') return 'success'
      if (type === 'Rejected All') return 'danger'
      return 'warning'
    },

    parseUserAgent(ua) {
      if (!ua) return 'Unknown'
      // Simple parsing - you might want to use a library like ua-parser-js
      let browser = 'Unknown'
      let os = 'Unknown'
      
      if (ua.includes('Chrome')) browser = 'Chrome'
      else if (ua.includes('Safari')) browser = 'Safari'
      else if (ua.includes('Firefox')) browser = 'Firefox'
      else if (ua.includes('Edge')) browser = 'Edge'
      
      if (ua.includes('Windows')) os = 'Windows'
      else if (ua.includes('Mac')) os = 'macOS'
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'
      else if (ua.includes('Android')) os = 'Android'
      else if (ua.includes('Linux')) os = 'Linux'
      
      return `${browser} / ${os}`
    },

    viewDetails(log) {
      this.selectedLog = log
      this.showDetailsModal = true
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchLogs()
      }, 500)
    },

    clearFilters() {
      this.filters = {
        search: '',
        consentType: { value: '', text: 'All Consent Types' },
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate()
      }
      this.fetchLogs()
    },

    refreshLogs() {
      this.fetchLogs()
      this.fetchStats()
    },

    async exportLogs() {
      try {
        const params = {
          format: 'csv',
          search: this.filters.search,
          type: this.filters.consentType.value, // Extract the value from the object
          startDate: this.filters.startDate,
          endDate: this.filters.endDate
        }
        
        // Use the service to export logs
        const response = await cookieConsentLogsService.exportLogs(params)
        
        if (response && response.success) {
          this.$store.dispatch('addNotification', {
            type: 'success',
            message: 'Export completed successfully.'
          })
        }
      } catch (error) {
        console.error('Error exporting logs:', error)
        this.$store.dispatch('addNotification', {
          type: 'error',
          message: 'Failed to export logs. Please try again.'
        })
      }
    }
  }
}
</script>

<style scoped>
.text-value-lg {
  font-size: 1.75rem;
  font-weight: 600;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Multiselect styles */
.multiselect__tags {
  min-height: 38px;
  border: 1px solid #d8dbe0;
  border-radius: 3px;
}

.multiselect__tag {
  background: #321fdb;
  color: white;
}

.multiselect__tag-icon:after {
  color: white;
}

.multiselect__tag-icon:hover {
  background: #2c1bc7;
}

.multiselect__placeholder {
  color: #768192;
  font-size: 14px;
}

.multiselect__input::placeholder {
  color: #768192;
  font-size: 14px;
}

.multiselect__option--highlight {
  background: #321fdb;
  color: white;
}

.multiselect__option--selected {
  background: #f0f3f5;
  color: #2c3e50;
  font-weight: bold;
}
</style>